import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { FinalCTA } from "@/components/site/CTAs";
import { Link } from "react-router-dom";

const HowItWorks = () => (
  <>
    <Seo
      title="Clearview — how it works"
      description="Connect once. Then just ask. No dashboard to learn, no setup project — Clearview reads your numbers and gives you a straight call."
      path="/how-it-works"
    />
    <Header />

    <section className="ph">
      <div className="container">
        <Reveal>
          <div className="eyebrow eyebrow-light">How it works</div>
          <h1 style={{ marginTop: 14 }}>
            Connect once.<br /><span className="gtext">Then just ask.</span>
          </h1>
          <p className="lead">
            No dashboard to learn, no setup project, nothing to operate. You connect your accounting software once — and from then on you ask it questions like you'd ask a sharp second-in-command.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="pad">
      <div className="container narrow-860">
        <Reveal className="step">
          <div className="n"><span>1</span></div>
          <div>
            <h3>Connect read-only</h3>
            <p>Link Xero, QuickBooks or WorkflowMax in about two minutes — and your bank if you want a sharper cash picture. The connection is <b>read-only</b>: Clearview can see your numbers but can't move a cent or change anything in your books.</p>
          </div>
        </Reveal>
        <Reveal className="step">
          <div className="n"><span>2</span></div>
          <div>
            <h3>It reads the whole business, continuously</h3>
            <p>Cash, margin, debtors, what's owed, what's coming — pulled together across every system and kept current. Not one app's slice of the picture. The complete read, ready the moment you need it.</p>
          </div>
        </Reveal>
        <Reveal className="step">
          <div className="n"><span>3</span></div>
          <div>
            <h3>You ask. It takes a position</h3>
            <p>Ask a real question — "can we afford to hire," "is this job actually making money," "can we afford the $90k van" — any time, day or night. You get a straight answer: the verdict, the working behind it, costed options, and the one move that matters most. Not a chart. A call.</p>
          </div>
        </Reveal>
        <Reveal className="step flag">
          <div className="n"><span>4</span></div>
          <div>
            <h3>And it tells you when it can't</h3>
            <p>If your numbers haven't reconciled or the signal's too thin to trust, Clearview says so and refuses — instead of guessing. That honesty is the whole point of an independent second opinion, and it's the one thing your software's built-in AI will never do.</p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="band-grey pad">
      <div className="container narrow-860">
        <Reveal className="center">
          <div className="eyebrow">What an answer looks like</div>
          <h2 style={{ fontSize: "clamp(26px,4vw,38px)", color: "var(--navy)", marginTop: 14 }}>
            You ask in plain English.
          </h2>
        </Reveal>
        <Reveal className="pcard-mini">
          <div className="top">
            <span>Ask Clearview</span>
            <span>Synced from Xero · confidence high</span>
          </div>
          <div className="body">
            <div className="ask"><span>Can we afford a new van — it's $90k?</span></div>
            <div className="lab">Answer</div>
            <div className="ans">
              <span className="no">Not right now.</span> After ~$19k set aside for BAS/PAYG/super you're at $155k. A $90k van drops you to $65k — barely a month of cover. Chase the $14,218 overdue first; it's 16% of the van sitting unpaid.
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pad">
      <div className="container narrow-860">
        <Reveal>
          <div className="reassure">
            <div className="ic">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2 4 5v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V5l-8-3Z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
                <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h3>"Is it safe?" — yes, by design.</h3>
              <p>
                Clearview is <b>read-only by architecture</b>. It can read your figures to write you a read; it physically cannot move money, pay anyone, or alter your books. See exactly how the connection works, and how your data is handled, on the <Link to="/security">security page</Link>.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FinalCTA
      title="See it read your business."
      highlight="Ask anything."
      body="Connect read-only and ask your first real question in the next few minutes."
      testIdPrefix="how-final"
    />

    <Footer />
  </>
);

export default HowItWorks;
